import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICES } from "@/data/services";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Loader2, CalendarCheck } from "lucide-react";

const EMPTY = {
  name: "", phone: "", email: "", service_type: "",
  address: "", preferred_date: "", preferred_time: "", details: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, service_type: e.detail }));
    window.addEventListener("prefill-service", handler);
    return () => window.removeEventListener("prefill-service", handler);
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service_type) {
      toast.error("Please fill in your name, phone and the service you need.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await api.post("/bookings", payload);
      setDone(true);
      setForm(EMPTY);
      toast.success("Request received! We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="py-20 lg:py-28 bg-slate-900" data-testid="booking-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2 text-white">
            <p className="text-sm uppercase tracking-widest text-accent font-semibold mb-3">Book Now</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold tracking-tight">
              Schedule a Service Call
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Tell us what you need and when. We'll confirm your appointment and send a crew that
              gets it done right.
            </p>
            <ul className="mt-8 space-y-4 text-slate-300 text-sm">
              <li className="flex items-center gap-3"><CalendarCheck className="h-5 w-5 text-accent" /> Pick your preferred date & time</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-accent" /> Low-cost, no-obligation estimate</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-accent" /> Fast confirmation</li>
            </ul>

            <div className="mt-8 rounded-lg border border-amber-400/40 bg-amber-400/10 p-4" data-testid="estimate-disclaimer">
              <p className="text-xs uppercase tracking-widest text-amber-300 font-semibold mb-1.5">Estimate Disclaimer</p>
              <p className="text-sm text-slate-200 leading-relaxed">
                We charge a <strong className="text-white">$149 trip fee</strong> to cover our time in case you
                decide not to have us do the work. If you move forward, that amount is
                <strong className="text-white"> deducted from your final bill.</strong>
              </p>
            </div>
          </div>

          <div className="lg:col-span-3 bg-white rounded-2xl p-6 sm:p-8 shadow-xl" data-service-prefill="">
            {done ? (
              <div className="text-center py-12" data-testid="booking-success">
                <div className="h-16 w-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="h-9 w-9 text-green-500" />
                </div>
                <h3 className="text-2xl font-display font-bold text-slate-900">Request Received!</h3>
                <p className="text-slate-500 mt-2">Thanks for choosing Prowess Services. We'll reach out shortly to confirm.</p>
                <Button className="mt-6 bg-accent hover:bg-blue-700" onClick={() => setDone(false)} data-testid="booking-another">
                  Book Another Service
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input id="name" data-testid="booking-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="John Smith" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input id="phone" data-testid="booking-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(555) 123-4567" className="mt-1.5" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" data-testid="booking-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Service Needed *</Label>
                    <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
                      <SelectTrigger className="mt-1.5" data-testid="booking-service">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((s) => (
                          <SelectItem key={s.id} value={s.name} data-testid={`booking-service-${s.id}`}>{s.name}</SelectItem>
                        ))}
                        <SelectItem value="Other / Not Sure">Other / Not Sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Job Address</Label>
                  <Input id="address" data-testid="booking-address" value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="123 Main St, Houston, TX" className="mt-1.5" />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="date">Preferred Date</Label>
                    <Input id="date" type="date" data-testid="booking-date" value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="time">Preferred Time</Label>
                    <Input id="time" type="time" data-testid="booking-time" value={form.preferred_time} onChange={(e) => update("preferred_time", e.target.value)} className="mt-1.5" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="details">Job Details</Label>
                  <Textarea id="details" data-testid="booking-details" value={form.details} onChange={(e) => update("details", e.target.value)} placeholder="Describe the work you need done..." rows={4} className="mt-1.5" />
                </div>

                <Button type="submit" disabled={loading} data-testid="booking-submit" className="w-full bg-accent hover:bg-blue-700 text-white font-semibold py-6 text-base">
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Submitting...</> : "Request My Service Call"}
                </Button>
                <p className="text-xs text-slate-400 text-center">By submitting, you acknowledge the $149 trip fee (credited to your final bill if you proceed). We'll never share your information.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
